/* eslint-disable max-len */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { store } from '../../../redux_setup/store';
import {
  setSelectedSavedWorkout,
  setActiveModal,
  setIsDragging,
  setDragSource,
  setDraggedItem,
  setOnBeforeCapture,
  setIsCloneDragging,
  setCalendarItems,
  setNonDateBasedProgramItems,
  setCombineWithInfo,
  updateCalendarItemsOrder,
  addPreSavedItemToCalendarItemsArray,
  addPreSavedItemToProgramItemsArray,
  setPlaceholderInfo,
  setDragIsInProgress,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
  moveExerciseBetweenDates,
  removeExpandedItem,
} from '../ducks/calendarActions';
import generateUniqueId from '../../../shared/utils/generateUniqueId';
import { SUPERSET_DEFAULTS } from '../../../shared/constants';
import { caches } from '../utils/virtualizationCache';

const useDragManager = () => {
  const dispatch = useDispatch();

  // Get calendar items without subscribing
  const getCalendarItems = useCallback((date, programDayNumber) => {
    const state = store.getState();
    const { programType } = state.calendar.data;
    const dateFormatted = moment(date).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      return state.calendar.data.nonDateBasedProgramItems[
        programDayNumber
      ]?.exercises || [];
    }
    return state.calendar.data.calendarItems[dateFormatted]?.exercises || [];
  }, []);

  const getAllExerciseCalendarItemsForWeekOrDay = useCallback((itemsDictionary) => {
    const calendarItemsArray = Object.values(itemsDictionary);

    // Use flatMap to extract and flatten all exercises arrays into one array
    const allExercises = calendarItemsArray.flatMap((itm) => itm.exercises || []);

    return allExercises;
  }, []);

  // Get expanded items without subscribing
  const getExpandedItems = useCallback(() => {
    const state = store.getState();
    return state.calendar.data.expandedItems;
  }, []);

  // Get other data without subscribing
  const getDragData = useCallback(() => {
    const state = store.getState();
    return {
      currentUser: state.auth.data.currentUser,
      selectedCalendar: state.calendar.data.selectedReduxCalendar,
      selectedAthlete: state.calendar.data.selectedReduxAthlete,
      savedWorkouts: state.calendar.data.savedWorkouts,
      calendarItemContainers: state.calendar.data.calendarItemContainers,
      programType: state.calendar.data.programType,
    };
  }, []);

  // Helper function to get calendar items for week-day containers
  const getCalendarItemsForDroppable = useCallback((droppableId, selectedDate, selectedProgramDayNumber) => {
    if (droppableId === 'programmedCalendarItems') {
      return getCalendarItems(selectedDate, selectedProgramDayNumber);
    }
    // Handle week-day containers
    if (droppableId.startsWith('week-day-')) {
      /** the day index will only be needed for free form program view
       * in free form view the id has an index if not in free form view
       * the weekday droppable id has the actually date string attached
       */
      const dayIndex = parseInt(droppableId.replace('week-day-', ''), 10);
      const targetDate = droppableId.replace('week-day-', '');
      return getCalendarItems(targetDate, dayIndex);
    }

    // Handle sidebar containers
    const dragData = getDragData();
    return dragData.calendarItemContainers[droppableId] || [];
  }, [getCalendarItems, getDragData]);

  // Helper function to get the correct date for a droppable container
  const getDateForDroppable = useCallback((droppableId, selectedDate) => {
    if (droppableId === 'programmedCalendarItems') {
      return selectedDate;
    }

    // Handle week-day containers
    if (droppableId.startsWith('week-day-')) {
      const dateForDroppable = droppableId.replace('week-day-', '');
      return dateForDroppable;
    }

    // Default to selected date for other containers
    return selectedDate;
  }, []);

  // Get next available grouping letter
  const getNextAvailableGrouping = useCallback((calendarItems) => {
    const alphabet = Object.keys(SUPERSET_DEFAULTS);

    // return grouping letter and filter them -  checks if the current letter is the first time
    // that letter appears in the array. If it is, the condition will be true, and that letter
    // will be included in the filtered result.
    const usedLetters = calendarItems
      .map((item) => item.groupingLetter && item.groupingLetter.toUpperCase())
      .filter((val, index, self) => val && self.indexOf(val) === index);

    const nextLetter = alphabet.find((letter) => usedLetters.indexOf(letter) === -1) || 'A';

    return {
      groupingLetter: nextLetter,
      groupingColorCode: SUPERSET_DEFAULTS[nextLetter],
    };
  }, []);

  const handleCombineLogic = useCallback((result, sourceItems, targetItems, dragData, selectedDate, selectedProgramDayNumber, viewType) => {
    console.log('selected program day number:', selectedProgramDayNumber);
    console.log('viewType:', viewType);
    const { source, combine, draggableId } = result;
    const { accountCode } = dragData.currentUser;
    const sourceDate = getDateForDroppable(source.droppableId, selectedDate);
    const targetDate = getDateForDroppable(combine.droppableId, selectedDate);

    // Track original items
    const originalSourceItems = sourceItems.map((item) => ({ ...item }));
    const originalTargetItems = targetItems.map((item) => ({ ...item }));

    // Create new array to manipulate items and set with grouping letters and colors
    const newSourceItems = [...sourceItems];
    const newTargetItems = [...targetItems];
    let sourceItem = {};
    const draggableIdString = combine.draggableId;
    const cleanDraggableAssignedId = draggableIdString.replace(/-programmedCalendarItems$/, '');

    // Check if source is from sidebar
    const sidebarSources = [
      'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
      'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
      'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
      'tagCalendarItems', 'accessoryCalendarItems',
    ];

    if (sidebarSources.includes(source.droppableId)) {
      const sideBarItemsWhereSourceComingFrom = dragData.calendarItemContainers[source.droppableId];
      const removedDraggableIdMinusSideBarFlag = draggableId.match(/^(.*)-sideBar$/i)
        ? draggableId.match(/^(.*)-sideBar$/i)[1]
        : null;
      const removed = sideBarItemsWhereSourceComingFrom.find(
        (item) => item.id === removedDraggableIdMinusSideBarFlag,
      );

      sourceItem = {
        ...removed,
        title: removed.name,
        exerciseId: parseInt(removed.id, 10),
        id: `${removed.id}-${generateUniqueId()}`,
        assignedId: `${removed.id}-${generateUniqueId()}`,
        preSave: true,
        source: 'sidebar',
      };
    } else {
      /** Source item is in programmed calendar items */
      sourceItem = newSourceItems[source.index];
    }

    const targetItemIndex = targetItems.findIndex(
      (item) => item.assignedId.toString() === cleanDraggableAssignedId.toString(),
    );
    const targetItem = targetItems[targetItemIndex];
    const originalSource = originalSourceItems[source.index];
    const originalTargetIndex = originalTargetItems.findIndex(
      (item) => item.assignedId.toString() === cleanDraggableAssignedId.toString(),
    );
    const originalTarget = originalTargetItems[originalTargetIndex];

    /** can not combine with session break items */
    if (targetItem?.type === 'session_break' || sourceItem?.type === 'session_break') {
      return;
    }
    if (sourceItem?.type === 'session_break' && sourceItem.source === 'sidebar') {
      return;
    }
    // Handle grouping logic
    if (!targetItem.groupingLetter && !sourceItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      const { groupingLetter, groupingColorCode } = getNextAvailableGrouping(newTargetItems);
      targetItem.groupingLetter = groupingLetter;
      targetItem.groupingColorCode = groupingColorCode;
      targetItem.shouldUpdateGrouping = groupingLetter;
      sourceItem.groupingLetter = groupingLetter;
      sourceItem.groupingColorCode = groupingColorCode;
      sourceItem.shouldUpdateGrouping = groupingLetter;
    }

    if (targetItem.groupingLetter && !sourceItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      sourceItem.groupingLetter = targetItem.groupingLetter;
      sourceItem.groupingColorCode = targetItem.groupingColorCode;
      sourceItem.shouldUpdateGrouping = targetItem.groupingLetter;
    }

    if (sourceItem.groupingLetter && !targetItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      const { groupingLetter, groupingColorCode } = getNextAvailableGrouping(newTargetItems);
      targetItem.groupingLetter = groupingLetter;
      targetItem.groupingColorCode = groupingColorCode;
      targetItem.shouldUpdateGrouping = groupingLetter;
      sourceItem.groupingLetter = groupingLetter;
      sourceItem.groupingColorCode = groupingColorCode;
      sourceItem.shouldUpdateGrouping = groupingLetter;
    }

    if (sourceItem.groupingLetter && targetItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      sourceItem.groupingLetter = targetItem.groupingLetter;
      sourceItem.groupingColorCode = targetItem.groupingColorCode;
      sourceItem.shouldUpdateGrouping = targetItem.groupingLetter;
    }

    if (source.droppableId === combine.droppableId) {
      /** Move within the same array, adjusting for index shifts */
      const [movedItem] = newSourceItems.splice(source.index, 1);
      const adjustedTargetIndex = source.index < targetItemIndex
        ? targetItemIndex - 1
        : targetItemIndex;

      newSourceItems.splice(adjustedTargetIndex + 1, 0, movedItem);
    } else {
      /** moving between droppable containers */
      if (sourceItem.source === 'sidebar') {
        /** Coming from sidebar — don't remove, just insert into target */
        newTargetItems.splice(targetItemIndex + 1, 0, sourceItem);

        dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));

        /** this will trigger an update call in the appropriate tables with new grouping letter and color */
        dispatch(setCombineWithInfo({
          /** if either the target item of the sourceItem grouping letter is the same */
          /** as the original then we don't need to set something to trigger an update */
          target: originalTarget?.groupingLetter === targetItem?.groupingLetter ? null : targetItem,
          source: originalSource?.groupingLetter === sourceItem?.groupingLetter ? null : [sourceItem],
        }));
      }
      if (sourceItem.source !== 'sidebar') {
        /** Move from source list to target list */
        const [movedItem] = newSourceItems.splice(source.index, 1);
        newTargetItems.splice(targetItemIndex + 1, 0, movedItem);
      }
    }

    /** Update Redux and setCombineWithInfo and hit moveExerciseBetween days if necessary
     * reorder endpoint will get hit on update if the order has changed
     * this will set the new values on the items that got updated and it will
     * set the new order so that the source item gets removed and replaced
     * right underneath the target item. */
    if (dragData.programType === 'free-form-program') {
      console.log('FINISH LOGIC HERE');
      // dispatch(setNonDateBasedProgramItems(newSourceItems, sourceDayNumber));
      // dispatch(setNonDateBasedProgramItems(newTargetItems, targetDayNumber));
    } else {
      if (source.droppableId === combine.droppableId && sourceItem.source !== 'sidebar') {
        dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));

        /** this will trigger an update call in the appropriate tables with new grouping letter and color */
        dispatch(setCombineWithInfo({
          /** if either the target item of the sourceItem grouping letter is the same */
          /** as the original then we don't need to set something to trigger an update */
          target: originalTarget?.groupingLetter === targetItem?.groupingLetter ? null : targetItem,
          source: originalSource?.groupingLetter === sourceItem?.groupingLetter ? null : [sourceItem],
        }));
      }
      if (source.droppableId !== combine.droppableId && sourceItem.source !== 'sidebar') {
        const assignmentId = sourceItem?.assignedId;
        const newEndItems = newTargetItems;
        dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
        dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));
        if (!sourceItem.preSave) {
          dispatch(moveExerciseBetweenDates(
            accountCode,
            assignmentId,
            targetDate,
            sourceDate,
            targetDate,
            newEndItems,
            {
              onSuccess: () => {
              /** setting items here again helps make sure we don't get a temporary flicker back to
               * the presaved grouping items and colors
               */
                dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
                dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));

                /** this will trigger an update call in the appropriate tables with new grouping letter and color */
                dispatch(setCombineWithInfo({
                /** if either the target item of the sourceItem grouping letter is the same */
                /** as the original then we don't need to set something to trigger an update */
                  target: originalTarget?.groupingLetter === targetItem?.groupingLetter ? null : targetItem,
                  source: originalSource?.groupingLetter === sourceItem?.groupingLetter ? null : [sourceItem],
                }));
              },
              onError: () => {},
            },
          )).catch(() => {
            dispatch(setCalendarItems(originalSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
            dispatch(setCalendarItems(originalTargetItems, moment(targetDate).format('YYYY-MM-DD')));
          });
        }
      }
    }
  }, [dispatch, getNextAvailableGrouping]);

  const handleRegularDrag = useCallback((result, currentDayItems, dragData, selectedDate, selectedProgramDayNumber) => {
    const { source, destination, draggableId } = result;
    const { accountCode } = dragData.currentUser;
    const destinationDate = getDateForDroppable(destination.droppableId, selectedDate);
    const endpointFormattedDate = moment(destinationDate).format('YYYY-MM-DD');
    const draggableIdString = draggableId;
    const cleanDraggableAssignedId = draggableIdString.replace(/-programmedCalendarItems$/, '');

    if (!destination) return;

    /** Early returns for same droppable sources */
    const sameDroppableSources = [
      'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
      'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
      'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
      'tagCalendarItems', 'additional-items-container',
    ];

    if (sameDroppableSources.includes(source.droppableId)
      && source.droppableId === destination.droppableId) {
      return;
    }

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const start = getCalendarItemsForDroppable(source.droppableId, selectedDate, selectedProgramDayNumber);
    const end = getCalendarItemsForDroppable(destination.droppableId, selectedDate, selectedProgramDayNumber);

    if (start === end) {
      /** Reordering within the same list */

      /** NEW LOGIG FOR ACCOMMODATING MOVING SESSION BREAKS AS CHUNK */
      /** we only need to handle this for same day reordering because we
       * cannot support moving session breaks as a chunk on the back end from
       * day to day
       */
      const originalItems = Array.from(start);
      const newItems = Array.from(start);

      const currentItem = originalItems[source.index];

      /** logic to accommodate moving a chunk of session break items (for single day view only) */
      const getSessionBreakChunk = (items) => {
        const indexOfDraggedItem = items.findIndex(
          (i) => i.assignedId === currentItem.assignedId,
        );
        const startItem = items[indexOfDraggedItem];
        if (!startItem || startItem.type !== 'session_break') return [];

        const resultItems = [startItem];
        // eslint-disable-next-line no-plusplus
        for (let i = indexOfDraggedItem + 1; i < items.length; i++) {
          const calItem = items[i];
          if (calItem.type === 'session_break') break;
          resultItems.push(calItem);
        }
        return resultItems;
      };

      const sessionBreakArrayToBeMoved = currentItem?.type === 'session_break' ? getSessionBreakChunk(originalItems, source.index) : [];

      /** Determine the chunk to move */
      const chunk = sessionBreakArrayToBeMoved?.length
        ? sessionBreakArrayToBeMoved
        : [newItems[source.index]];

      if (!chunk || chunk.length === 0) return;

      /** Remove all items in the chunk from the list */
      const filteredItems = newItems.filter((item) => !chunk.includes(item));

      /** Insert the chunk at the destination index */
      const insertIndex = destination.index;
      let finalInsertIndex = insertIndex;

      if (
        sessionBreakArrayToBeMoved?.length
       && destination.index > source.index
      ) {
        /** Adjust only when moving the chunk down */
        finalInsertIndex = insertIndex - (sessionBreakArrayToBeMoved.length - 1);
      }
      filteredItems.splice(finalInsertIndex, 0, ...chunk);

      /** some times we can scoop up hidden session break items unintentionally
       * we need to account for that in the logic below and make sure they are handle
       * properly. The logic below takes care of this scenario...
       */
      /** STEP 1 — Get original and new chunks */
      const originalChunk = getSessionBreakChunk(originalItems, source.index);
      const newChunkAfterInsert = getSessionBreakChunk(filteredItems, destination.index);

      /** STEP 2 — Find items that were unintentionally added to the chunk */
      const unintentionallyMovedItems = [];

      newChunkAfterInsert.forEach((newItem) => {
        const wasInOriginal = originalChunk.some(
          (origItem) => origItem.assignedId === newItem.assignedId,
        );

        if (!wasInOriginal) {
          unintentionallyMovedItems.push(newItem);
        }
      });

      /** Here we need to create a reordered array incase there are
       * unintentionally scoop up hidden items in the session break chunk
       * if it is the case then we will move the unintentionally moved items
      as a unit above the session break they are currently under in the filtered items
      then we can make a finalItems array to send to the endpoint */

      let finalItems = filteredItems;

      if (unintentionallyMovedItems.length > 0) {
        /** Extract moved IDs as strings to avoid type mismatches */
        const movedIds = unintentionallyMovedItems.map((item) => String(item.assignedId));
        /** Remove unintentionally moved items from filteredItems */
        const newFilteredItems = filteredItems.filter(
          (item) => !movedIds.includes(String(item.assignedId)),
        );
        /** Find the index of the dragged session break in the new array */
        const draggedIndex = newFilteredItems.findIndex(
          (item) => String(item.assignedId) === String(cleanDraggableAssignedId),
        );

        if (draggedIndex === -1) {
          console.warn('Dragged session break not found in filteredItems.');
        } else {
          /** Insert the unintentionally moved items above the dragged session break */
          newFilteredItems.splice(draggedIndex, 0, ...unintentionallyMovedItems);
        }

        /** Replace filteredItems with the new ordered array */
        finalItems = newFilteredItems;
      }

      /** Build the reordered list of assigned IDs */
      const newOrderedAssignmentIds = [];
      finalItems.forEach((calendarItem) => {
        if (typeof calendarItem.assignedId !== 'string') {
          newOrderedAssignmentIds.push(calendarItem.assignedId);
        }
      });

      const submissionObject = {
        date: endpointFormattedDate,
        order: newOrderedAssignmentIds,
      };

      if (newOrderedAssignmentIds.length) {
        if (dragData.programType === 'free-form-program') {
          dispatch(updateCalendarItemsOrder(accountCode, submissionObject, originalItems));
          dispatch(setNonDateBasedProgramItems(finalItems, selectedProgramDayNumber));
        } else {
          dispatch(updateCalendarItemsOrder(accountCode, submissionObject, originalItems));
          dispatch(setCalendarItems(finalItems, endpointFormattedDate));
        }
      }
    } else {
      /** Moving between lists */
      const startItems = Array.from(start);
      const endItems = Array.from(end);

      const sidebarSources = [
        'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
        'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
        'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
        'tagCalendarItems', 'accessoryCalendarItems',
      ];

      if (sidebarSources.includes(source.droppableId)) {
        // Handle drag from sidebar to calendar
        const removedDraggableIdMinusSideBarFlag = draggableId.match(/^(.*)-sideBar$/i)
          ? draggableId.match(/^(.*)-sideBar$/i)[1]
          : null;
        const removed = startItems.find((item) => item.id === removedDraggableIdMinusSideBarFlag);

        const copiedItem = {
          ...removed,
          title: removed.name,
          exerciseId: parseInt(removed.id, 10),
          id: `${removed.id}-${generateUniqueId()}`,
          assignedId: `${removed.id}-${generateUniqueId()}`,
          preSave: true,
        };

        if (dragData.programType === 'free-form-program') {
          dispatch(
            addPreSavedItemToProgramItemsArray(
              copiedItem,
              selectedProgramDayNumber,
              destination.index,
              destination.droppableId,
            ),
          );
        } else {
          dispatch(
            addPreSavedItemToCalendarItemsArray(
              copiedItem,
              endpointFormattedDate,
              destination.index,
              destination.droppableId,
            ),
          );
        }
      } else {
        // Handle drag between calendar containers (including week-day to week-day)
        const removed = startItems[source.index];

        // Update both source and destination
        const sourceDate = getDateForDroppable(source.droppableId, selectedDate);
        // eslint-disable-next-line no-shadow
        const destinationDate = getDateForDroppable(destination.droppableId, selectedDate);

        // Check if this is a cross-date movement in week view
        const isCrossDateMovement = sourceDate !== destinationDate
          && source.droppableId.startsWith('week-day-')
          && destination.droppableId.startsWith('week-day-')
          && dragData.programType !== 'free-form-program';

        if (isCrossDateMovement) {
          // Use new API endpoint for cross-date movement
          const assignmentId = removed.assignedId;
          const targetDate = moment(destinationDate).format('YYYY-MM-DD');

          // Optimistic update: Update local state immediately
          const newStartItems = [...startItems];
          newStartItems.splice(source.index, 1);
          const newEndItems = [...endItems];
          newEndItems.splice(destination.index, 0, removed);

          dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
          dispatch(setCalendarItems(newEndItems, moment(destinationDate).format('YYYY-MM-DD')));

          if (!removed.preSave) {
          // Call API to persist the change
            dispatch(moveExerciseBetweenDates(
              accountCode,
              assignmentId,
              targetDate,
              sourceDate,
              destinationDate,
              newEndItems,
            )).catch(() => {
            // Revert changes on error
              dispatch(setCalendarItems(startItems, moment(sourceDate).format('YYYY-MM-DD')));
              dispatch(setCalendarItems(endItems, moment(destinationDate).format('YYYY-MM-DD')));
            });
          }
        } else {
          // Original logic for same-date or non-week-view movements
          const newStartItems = [...startItems];
          newStartItems.splice(source.index, 1);
          const newEndItems = [...endItems];
          newEndItems.splice(destination.index, 0, removed);

          if (dragData.programType === 'free-form-program') {
            // Update source container
            dispatch(setNonDateBasedProgramItems(newStartItems, source.index));
            // Update destination container
            dispatch(setNonDateBasedProgramItems(newEndItems, destination.index));
          } else {
            // Update source container
            dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
            // Update destination container
            dispatch(setCalendarItems(newEndItems, moment(destinationDate).format('YYYY-MM-DD')));
          }
        }
      }
    }
  }, [dispatch, getDateForDroppable]);

  const handleDragEnd = useCallback((result) => {
    const { viewType } = store.getState().calendar.data;
    // Clean up drag state first

    dispatch(setDragIsInProgress(false));
    dispatch(setPlaceholderInfo(null));
    dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(null));

    // onBeforeCapture event allows us to dynamically set the height
    // of the droppable container so we can dynamically show the message 'drag and drop here'
    // and allows us to dynamically add the extra space and dynamically
    // show the dropzone on first item drag

    dispatch(setOnBeforeCapture(null));

    // setIsCloneDragging(false) needs to stay at top of this function
    dispatch(setIsCloneDragging(false));
    dispatch(setIsDragging(false));
    dispatch(setDragSource(null));
    dispatch(setDraggedItem(null));

    const {
      source, destination, combine, draggableId,
    } = result;

    // Dropped outside the droppable area
    if (!destination && !combine) return;

    const dragData = getDragData();
    const { selectedDate } = store.getState().calendar.data;
    const { selectedProgramDayNumber } = store.getState().calendar.data;

    // If dragging from templates-sidebar, open the
    // load programs modal and set the selected workout
    if (source.droppableId === 'templates-sidebar') {
      const workoutId = draggableId.replace('-sideBar', '');
      const draggedWorkout = dragData.savedWorkouts.find((w) => w.id === parseInt(workoutId, 10));
      dispatch(setSelectedSavedWorkout(draggedWorkout));
      dispatch(setActiveModal('loadProgramsModal'));
      return;
    }

    // For day view, destination is always the current day
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);

    // Handle combine (superset creation)
    if (combine) {
      const sourceItems = getCalendarItemsForDroppable(source.droppableId, selectedDate, selectedProgramDayNumber);
      const targetItems = getCalendarItemsForDroppable(combine.droppableId, selectedDate, selectedProgramDayNumber);
      //  handleCombineLogic(result, currentDayItems, dragData, selectedDate, selectedProgramDayNumber);
      handleCombineLogic(result, sourceItems, targetItems, dragData, selectedDate, selectedProgramDayNumber, viewType);
      return;
    }

    // Handle regular drag & drop
    handleRegularDrag(result, currentDayItems, dragData, selectedDate, selectedProgramDayNumber);
  }, [dispatch, getCalendarItems, getDragData, handleCombineLogic, handleRegularDrag, getCalendarItemsForDroppable, getDateForDroppable]);

  const handleDragStart = useCallback((start) => {
    const { selectedDate } = store.getState().calendar.data;
    const { viewType } = store.getState().calendar.data;
    const { programType } = store.getState().calendar.data;
    const { selectedProgramDayNumber } = store.getState().calendar.data;
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);
    const { beforeRecurringWorkouts } = store.getState().calendar.data;
    const formatDate = (date) => `${moment(date).format('YYYY-MM-DD')}`;

    const sourceDroppableId = start.source.droppableId;
    const currentDayNumberOrDate = programType === 'free-form-program' ? selectedProgramDayNumber : formatDate(selectedDate);

    const getHasBefore = () => {
      if (viewType === 'weekView') {
        if (programType === 'free-form-program') {
          const numberString = sourceDroppableId.split('-').pop();
          const weekDayIndexNumber = parseInt(numberString, 10);
          return !!(beforeRecurringWorkouts[weekDayIndexNumber] && beforeRecurringWorkouts[weekDayIndexNumber].length);
        }
        if (programType === 'calendar') {
          const weekDate = sourceDroppableId.match(/\d{4}-\d{2}-\d{2}/)?.[0] || null;
          return !!(beforeRecurringWorkouts[formatDate(weekDate)] && beforeRecurringWorkouts[formatDate(weekDate)].length);
        }
      }
      return !!(beforeRecurringWorkouts[currentDayNumberOrDate] && beforeRecurringWorkouts[currentDayNumberOrDate].length);
    };

    /** using this data to set the size of the placeholder we will need
     * when dragging and dropping between containers
     */
    const hasBefore = getHasBefore();
    const { droppableId, index } = start.source;
    const height = caches[droppableId] ? caches[droppableId]?.getHeight(hasBefore ? index + 1 : index, 0) : 40;
    dispatch(setPlaceholderInfo({
      height,
      draggableId: start.draggableId,
      droppableId: start.source.droppableId,
    }));
    /** placeholder info logic ends */

    /** this state is only being used for the drop here section
     * we are not using the drop here section in week view so we don't need to
     * add week day logic below... this is only for day view so we can keep the
     * currentDayItems logic
     */
    if (currentDayItems.length <= 1 && viewType === 'dayView') {
      dispatch(setIsDragging(true));
      dispatch(setDragSource(start.source.droppableId));
    }
  }, [dispatch, getCalendarItems]);

  const handleOnBeforeCapture = useCallback((before) => {
    const { selectedDate } = store.getState().calendar.data;
    const calendarItemsDictionary = store.getState().calendar.data.calendarItems;
    const nonDateBasedProgramItemsDictionary = store.getState().calendar.data.nonDateBasedProgramItems;
    const { viewType } = store.getState().calendar.data;
    const { programType } = store.getState().calendar.data.programType;
    const { selectedProgramDayNumber } = store.getState().calendar.data;
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);

    if (viewType === 'weekView') {
      /** we ues this to detect if we should trigger
       * the onMouseEnter that allows us to scroll with a dragged
       * item in week view (over the side bar)
       * we had so use some speciall logic to trigger a drag and scroll
       * becuause react beautiful dnd does not support nexted scroll
       * and with out side bar it will not detect autoscroll on that side
       * so we have to hadd a funtion for it
       */
      dispatch(setDragIsInProgress(true));
    }

    /** This code is added so that we can track
     * what item is being dragged, we need to know if the item being dragged
     * is a session_break item and if so we are running some logic in the DraggableDivWrapper
     * to make sure that all items below a session break get expanded before the session_break
     * is dragged. When we add styling to onBeforeCapture we know is can create some performance
     * issues when there are a lot of items so this might need to be removed it
     * it's causing a big lag
     */
    const { draggableId } = before;
    const expandedItems = getExpandedItems();

    /** Step 1: Extract the numeric ID before the dash */
    const draggedAssignedId = draggableId.split('-')[0];
    const draggedItem = getAllExerciseCalendarItemsForWeekOrDay(programType === 'free-form-program' ? nonDateBasedProgramItemsDictionary : calendarItemsDictionary).find(
      (item) => item.assignedId.toString() === draggedAssignedId,
    );
    const itemIsExpanded = expandedItems[draggedItem?.assignedId] || false;
    // if (draggedItem?.type === 'session_break' && !itemIsExpanded) {
    if (draggedItem?.type === 'session_break') {
      const sourceDroppableId = programType === 'free-form-program' ? `week-day-${draggedItem.dayNumber}` : `week-day-${draggedItem.date}`;
      dispatch(setDraggedItem({ ...draggedItem, sourceDroppableId }));
      /** collapse the session break when user starts to drag
       * this will also trigger all items underneath the session break
       * to hide/collapse under it right before drag start
       */
      if (itemIsExpanded) {
        dispatch(removeExpandedItem(draggedItem));
      }
    }
    /** end of logic for tracking is dragged item is a session break */

    /** set onBeforeCapture is logic we use to display the drop here seciton in day view
     * we are only doing htis in day view so we dont' need this to fire in weekview
     */
    if (currentDayItems.length <= 1 && viewType === 'dayView') {
      dispatch(setOnBeforeCapture(before));
    }
  }, [dispatch, getCalendarItems]);

  return {
    handleDragEnd,
    handleDragStart,
    handleOnBeforeCapture,
  };
};

export default useDragManager;
